<template>
  <v-layout row justify-center align-center>
    <v-flex xs12>
      <v-card>
        <errors-list :errorMessage="error.errorMessage" />
        <v-form ref="form" @submit.prevent="onSubmit" color="green">
          <v-card-title class="headline">{{ $t('@bankDetailsForm/title') }}</v-card-title>
          <v-card-text>
            <v-select
              v-model="selectedBankId"
              :items="banks"
              :label="$t('@bankDetailsForm/bankListSelectLabel')"
              color="green darken-1"
            ></v-select>
            <v-text-field
              v-model="newBank.bankName"
              :label="$t('@bankDetailsForm/bankNameInputLabel')"
              :disabled="!!selectedBankId"
              :error-messages="validationError(error.errorField, errorFieldMap.bankName)"
              color="green darken-1"
            />
            <v-text-field
              v-model="newBank.clearingNumber"
              :label="$t('@bankDetailsForm/clearingInputLabel')"
              :disabled="!!selectedBankId"
              :error-messages="validationError(error.errorField, errorFieldMap.clearingNumber)"
              maxlength="6"
              color="green darken-1"
              @keypress="onKeyPress"
              @input="onChangeClearing"
            />
            <v-text-field
              v-model="newBank.bankNumber"
              :label="$t('@bankDetailsForm/accountNumberInputLabel')"
              :disabled="!!selectedBankId"
              :error-messages="validationError(error.errorField, errorFieldMap.bankNumber)"
              maxlength="10"
              color="green darken-1"
              @keypress="onKeyPress"
              @input="validateBankAccount"
            />
            <v-flex
              xs12
              class="text-lg-left grey--text text--darken-1 align-center caption"
              style="margin-top: -15px; position:absolute; line-height: 12px"
              :class="bankAccountMayBeInvalid ? 'd-flex' : 'd-none'"
            >
              <v-icon class="mr-1 d-inline" color="warning" size="1rem">warning</v-icon>
              {{ $t('@newContractForm/bankAccountMayBeInvalid') }}
            </v-flex>
            <v-checkbox
              v-model="updateAllContracts"
              :label="$t('@bankDetailsForm/updateAllActiveContractsCheckboxLabel')"
              color="green darken-1"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="red darken-1" flat="flat" @click.stop="reset">
              {{ $t('@bankDetailsForm/cancelButtonLabel') }}
            </v-btn>
            <v-btn color="green darken-1" flat="flat" :disabled="submitting" type="submit">
              {{ $t('@bankDetailsForm/submitButtonLabel') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { onKeyPress } from '../../utils';
import { getErrorMessage, validationError, getErrorObject } from '../../errorHandlers';
import ErrorsList from '../../components/ErrorsList';
import Kontonummer from 'kontonummer';

const errorFieldMap = {
  bankName: 'bankName',
  clearingNumber: 'clearingNumber',
  bankNumber: 'bankNumber',
};

export default {
  components: { ErrorsList },
  props: {
    contract: {
      type: Object,
    },
  },

  data() {
    return {
      submitting: false,
      newBank: {
        bankName: '',
        clearingNumber: '',
        bankNumber: '',
      },
      selectedBankId: null,
      updateAllContracts: false,
      bankAccountMayBeInvalid: false,
      banks: [],
      error: {
        errorMessage: '',
        errorField: '',
      },
      onKeyPress,
      validationError,
      errorFieldMap,
    };
  },

  computed: {
    customer() {
      return this.contract.customers.find((customer) => !!customer.isContactPerson);
    },
  },

  watch: {
    selectedBankId(v) {
      const bank = this.banks.find((b) => b.id === v);
      if (bank) {
        const { bankName, clearingNumber, bankNumber } = bank;
        this.newBank = { bankName, clearingNumber, bankNumber };
      } else {
        this.newBank = { bankName: '', clearingNumber: '', bankNumber: '' };
      }
      this.validateBankAccount();
    },
  },

  methods: {
    async fetchBanks() {
      if (!this.customer) {
        return;
      }
      try {
        const url = `/api/customers/${this.customer.id}/banks`;
        const { data } = await this.$http.get(url);

        this.banks = [
          { text: ' - ', value: null },
          ...data.map((el) => {
            el.text = `${el.bankNumber}(${el.bankName})`;
            el.value = el.id;
            return el;
          }),
        ];
      } catch (e) {
        this.error.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.newBank = { bankName: '', clearingNumber: '', bankNumber: '' };
      this.selectedBankId = null;
      this.error = { errorMessage: '', errorField: '' };
      this.updateAllContracts = false;
      this.bankAccountMayBeInvalid = false;
      this.$emit('dismiss');
    },

    async onSubmit() {
      const { bankName, clearingNumber, bankNumber } = this.newBank;

      try {
        if (!/^[\d]{4}[-]{1}[\d]{1}$|^[\d]{4}|^[\d]{5}$/.test(clearingNumber)) {
          this.error = {
            errorMessage: this.$t('@bankDetailsForm/invalidClearingNumberErrorMessage'),
            errorField: errorFieldMap.clearingNumber,
          };
          return;
        }

        if (!/^\d{6,10}$/.test(bankNumber)) {
          this.error = {
            errorMessage: this.$t('@bankDetailsForm/invalidAccountNumberErrorMessage'),
            errorField: errorFieldMap.bankNumber,
          };
          return;
        }

        this.error.errorField = '';

        this.submitting = true;
        const data = {
          bankName,
          clearingNumber,
          bankNumber,
        };

        if (this.updateAllContracts) {
          if (!this.customer) {
            this.error.errorMessage = this.$t(
              '@bankDetailsForm/missingRelatedWithContractCustomerErrorMessage',
            );
            return;
          }
          await this.$http.post(`/api/customers/${this.customer.id}/banks/all`, data);
        } else {
          await this.$http.post(`/api/insurances/${this.contract.id}/banks`, data);
        }
        this.fetchBanks();
        this.$emit('submitted');
        this.reset();
      } catch (e) {
        this.error = getErrorObject(e);
      } finally {
        this.submitting = false;
      }
    },

    onChangeClearing(e) {
      if (e.length === 5 && e.indexOf('-') === -1) {
        this.newBank.clearingNumber = e.substr(0, 4) + '-' + e.substr(-1);
      }
      this.validateBankAccount();
    },

    validateBankAccount() {
      this.bankAccountMayBeInvalid = false;
      if (!this.newBank.clearingNumber || !this.newBank.bankNumber) {
        return;
      }
      const { clearingNumber, bankNumber } = this.newBank;

      if (
        !/^[\d]{4}[-]{1}[\d]{1}$|^[\d]{4}|^[\d]{5}$/.test(clearingNumber) ||
        !/^\d{6,10}$/.test(bankNumber)
      ) {
        return;
      }

      try {
        const account = new Kontonummer(clearingNumber, bankNumber, {
          mode: 'lax',
        });

        if (!account.valid) {
          this.bankAccountMayBeInvalid = true;
        }
      } catch (e) {
        this.bankAccountMayBeInvalid = true;
      }
    },
  },

  async mounted() {
    await this.fetchBanks();
  },
};
</script>

<style scoped>
table.v-table tbody td.column-title {
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.54) !important;
}
</style>
